/**
 * URL-backed component state — ported VERBATIM from digitaladmin's
 * ui/src/lib/urlState.ts (their Step 97; our Step 12). The batching argument
 * below is the whole reason to port rather than rewrite. Only this header and
 * nothing else differs.
 *
 * Original header follows:
 *
 *
 * The one place `useSearchParams` is allowed to be touched directly. Every page that keeps a selection, a
 * tab or a filter in `useState` should keep it here instead, so a refresh doesn't lose your place and a
 * colleague can be sent exactly where you are.
 *
 * Works identically on staff pages and in the Volunteer Hub — it is plain React Router, and both domains
 * already carry the full path *and query* through their 401 redirect, so a deep link survives an expired
 * session. (The Hub's magic-link column is length-capped though — see CLAUDE.md.)
 *
 * WHY THIS EXISTS RATHER THAN EACH PAGE DOING IT
 *
 * `setSearchParams({ tab })` — the object form, and the obvious thing to write — **replaces the entire query
 * string**, silently dropping every other parameter. Both pages that kept the URL in sync before this hook
 * had that bug (`ScheduleView`'s week stepper, `ShowRequestsPage`'s tab switch): changing one thing wiped
 * the rest. Writing it 56 more times was not an option, so the merge lives here once.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * How a change should affect browser history.
 *
 * - `'push'` for identity — opening a record adds an entry, so Back closes the panel.
 * - `'replace'` for a view or a filter — otherwise leaving a page you've been filtering on takes nine Backs.
 */
export type UrlHistory = 'push' | 'replace';

export interface UrlStateOpts {
  /** Defaults to `'replace'`. Use `'push'` for "which record is open". */
  history?: UrlHistory;
  /**
   * The page's unsaved-changes guard. Return `true` to say "I've stashed this, don't navigate yet" — the
   * page then runs `commit` itself from `UnsavedChangesModal`'s Discard. Matches the `setPending(() => …)`
   * shape already used across the app.
   *
   * Without this, following a deep link or switching a tab throws away a draft with no prompt.
   */
  guard?: (commit: () => void) => boolean;
}

/**
 * Writes made in the same tick, before React has re-rendered, accumulate here.
 *
 * WHY THIS EXISTS — it is the whole correctness argument for the module.
 *
 * Every setter merges against the params it can see, which is the params **as of the last render**. So two
 * setters called one after the other in the same handler both merge against the same starting point, and the
 * second silently discards the first. That is not a rare edge: it is what a handler like
 *
 *     function selectScope(s) { setScope(s); if (!valid(layout)) setLayout(valid[0]) }
 *
 * does every time, and it shipped as a real bug — the Control Room picker needed its scope buttons clicking
 * twice, and one of the two values landed under the other's key.
 *
 * `useUrlPatch` exists for the case where the author KNOWS two keys move together. This exists because
 * relying on every author noticing does not work — it has to compose whether they noticed or not.
 *
 * Keyed on the params it was built from, so it self-invalidates the moment the router actually moves: a
 * write against a different URL starts a fresh accumulation rather than replaying stale keys.
 */
let batch: { seen: string; params: URLSearchParams } | null = null;

function base(live: URLSearchParams): URLSearchParams {
  const seen = live.toString();
  if (!batch || batch.seen !== seen) batch = { seen, params: new URLSearchParams(live) };
  return batch.params;
}

/**
 * Apply one or more key writes to this tick's accumulated params.
 *
 * Both hooks go through here so the delete-when-default rule and the batching can't drift apart, and so
 * `base()` is consulted exactly ONCE per commit — a per-key call would re-key the accumulator mid-loop and
 * any plain setter firing later in the same tick would then discard the whole patch.
 */
function apply(
  live: URLSearchParams,
  entries: Array<[key: string, value: string | null, fallback: string | null]>,
): URLSearchParams {
  // Merge, never assign — onto this tick's accumulated result, not onto a render-old snapshot. Everything
  // not named here is carried through untouched.
  const acc = base(live);
  for (const [key, value, fallback] of entries) {
    // A value equal to its default is absent, not spelled out — so a page at rest has a clean address, and
    // "no filter" and "the default filter" can't drift apart into two different URLs for one view.
    if (value === null || value === '' || value === fallback) acc.delete(key);
    else acc.set(key, value);
  }
  // A fresh object for the router: it must not be handed a reference a later write in this tick would mutate.
  return new URLSearchParams(acc);
}

function write(
  live: URLSearchParams,
  key: string,
  value: string | null,
  fallback: string | null,
): URLSearchParams {
  return apply(live, [[key, value, fallback]]);
}

/** `setX(v)` or `setX(prev => …)` — the same two forms `useState` gives, since call sites use both. */
type Update<T> = T | ((prev: T) => T);

function resolve<T>(next: Update<T>, prev: T): T {
  return typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
}

/**
 * A single string value in the query string.
 *
 * ```ts
 * const [status, setStatus] = useUrlState('status', 'outstanding')
 * const [openId, setOpenId] = useUrlState('entry', null, { history: 'push', guard: guardEdit })
 * ```
 *
 * The fallback is returned when the parameter is absent, and never written into the URL.
 *
 * **The value is `string`, deliberately not a narrowed literal union.** A URL can contain anything
 * somebody typed or an old link still carries, so a hook that promised `'requests' | 'gaps'` would be
 * lying. Callers validate what they read — which is the right shape anyway: a shared link with a stale
 * or typo'd value should fall back to the default, not render an empty page.
 */
export function useUrlState(
  key: string,
  fallback: string,
  opts?: UrlStateOpts,
): [string, (next: Update<string>) => void];
export function useUrlState(
  key: string,
  fallback: null,
  opts?: UrlStateOpts,
): [string | null, (next: Update<string | null>) => void];
// The implementation signature is loose on purpose and never visible to callers — the two overloads above
// shadow it. It has to be, because no single return type is assignable to both `[string, …]` and
// `[string | null, …]`, and the alternative is making every caller with a real default write `?? ''`.
// (digitaladmin carries an eslint-disable here; our config allows `any`, so the pragma would be unused.)
export function useUrlState(key: string, fallback: string | null, opts: UrlStateOpts = {}): any {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? fallback;

  // The setter is handed to callbacks and effects, so it must be stable across renders — but it needs the
  // CURRENT params to merge against. A ref keeps both: stable identity, live value. Without this, a setter
  // captured in a `useCallback` merges against whatever the query string was when that callback was built,
  // which quietly resurrects parameters the user has since cleared.
  const live = useRef(params);
  live.current = params;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const set = useCallback(
    (next: Update<string | null>) => {
      const { history = 'replace', guard } = optsRef.current;
      const commit = () => {
        // Resolved inside `commit`, against the params as they are AT COMMIT TIME — not when `set` was
        // called. That matters when a guard defers: the user may have changed something else before
        // pressing Discard, and a `prev =>` updater must see the newer value, not a stale snapshot.
        const prev = base(live.current).get(key) ?? fallback;
        setParams(write(live.current, key, resolve(next, prev), fallback), {
          replace: history === 'replace',
        });
      };
      // The guard gets first refusal. It returns true when it has stashed `commit` for its own modal to run.
      if (guard?.(commit)) return;
      commit();
    },
    [key, fallback, setParams],
  );

  return [value, set];
}

/**
 * A multi-select filter — comma-joined, because that survives a URL unencoded and reads plainly:
 * `?station=abc,def`. Order is the caller's; nothing is sorted, so a page that means "these three in this
 * order" keeps it.
 *
 * Empty means absent, so an all-cleared filter leaves no trace in the address bar.
 */
export function useUrlListState(
  key: string,
  opts: UrlStateOpts = {},
): [string[], (next: Update<string[]>) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);

  const value = useMemo(() => (raw ? raw.split(',').filter(Boolean) : []), [raw]);

  const live = useRef(params);
  live.current = params;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const set = useCallback(
    (next: Update<string[]>) => {
      const { history = 'replace', guard } = optsRef.current;
      const commit = () => {
        const raw = base(live.current).get(key);
        const prev = raw ? raw.split(',').filter(Boolean) : [];
        const joined = resolve(next, prev).filter(Boolean).join(',');
        setParams(write(live.current, key, joined || null, null), { replace: history === 'replace' });
      };
      if (guard?.(commit)) return;
      commit();
    },
    [key, setParams],
  );

  return [value, set];
}

/**
 * A text box whose value reaches the URL a beat after you stop typing.
 *
 * Filtering stays instant — the local value updates on every keystroke, exactly as `useState` did. Only the
 * address bar waits, so a long search doesn't re-render the page per character or churn the URL visibly.
 * Kai's call, 2026-08-01: catch up after ~400ms rather than on Enter, so a search you never press Enter on
 * is still in the link you send.
 *
 * Returns the same `[value, set]` shape as a `useState` search box, so converting one is a one-line change.
 */
export function useDebouncedUrlState(key: string, fallback = '', ms = 400): [string, (next: string) => void] {
  const [urlValue, setUrlValue] = useUrlState(key, fallback);
  const [local, setLocal] = useState(urlValue);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // What WE last pushed, so our own value coming back round doesn't look like an external change.
  const lastWritten = useRef<string | null>(null);

  useEffect(() => {
    if (urlValue === lastWritten.current) return;
    // Somebody else moved it — Back, Forward, or a Clear button elsewhere on the page. Follow it, and drop
    // anything still pending, or a stale keystroke would land 400ms later and undo the navigation.
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setLocal(urlValue);
  }, [urlValue]);

  // A pending keystroke must not outlive the page.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const set = useCallback(
    (next: string) => {
      setLocal(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        lastWritten.current = next;
        setUrlValue(next);
      }, ms);
    },
    [ms, setUrlValue],
  );

  return [local, set];
}

/**
 * Several keys at once, in one history entry.
 *
 * For the case a single `set` can't express: a filter row's Clear button, or picking a record that also
 * resets the page number. Doing those as two `set` calls would either leave a half-applied URL in history
 * or — worse, with two `replace`s in one tick — lose the first change entirely, since both would merge
 * against the same pre-change params.
 *
 * `null` deletes a key. Anything not mentioned is left alone.
 */
export function useUrlPatch(
  opts: UrlStateOpts = {},
): (patch: Record<string, string | string[] | null>) => void {
  const [params, setParams] = useSearchParams();

  const live = useRef(params);
  live.current = params;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  return useCallback(
    (patch: Record<string, string | string[] | null>) => {
      const { history = 'replace', guard } = optsRef.current;
      const commit = () => {
        const next = apply(
          live.current,
          Object.entries(patch).map(([key, v]) => [
            key,
            Array.isArray(v) ? v.filter(Boolean).join(',') || null : v,
            null,
          ]),
        );
        setParams(next, { replace: history === 'replace' });
      };
      if (guard?.(commit)) return;
      commit();
    },
    [setParams],
  );
}
